/**
 * Stands in for a low-pass stage when filtering is switched off in the ECG viewer. Returns the same array it was given, performing no work.
 *
 * @param data - ECG samples passed straight through.
 * @returns The input samples unchanged.
 */
export const Lowpass0Hz = function (data: number[]) {
  return data;
};

/**
 * Low-pass filters an ECG channel recorded at 250Hz at a 15Hz cutoff. Runs a hardcoded order-6 IIR recurrence, scaling each input by the filter gain and carrying six delayed input and output taps forward across the samples, then repeats the recurrence over the result in reverse for zero phase shift.
 *
 * @param data - ECG samples for one channel.
 * @returns The samples with content above 15Hz attenuated.
 */
export const Lowpass15Hz = function (data: number[]) {
  const dataLength = data.length;
  const filtData = new Array(dataLength);

  // *: Order 6
  const Nzeros = 6;
  const Npoles = 6;
  const GAIN = 4.32281957e4;
  const xv = new Array(Nzeros + 1).fill(0);
  const yv = new Array(Npoles + 1).fill(0);

  for (let i = 0; i < dataLength; i += 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = data[i] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] =
      xv[0] +
      xv[6] +
      6 * (xv[1] + xv[5]) +
      15 * (xv[2] + xv[4]) +
      20 * xv[3] +
      -0.2304689873 * yv[0] +
      1.7181353495 * yv[1] +
      -5.3861730594 * yv[2] +
      9.0983763199 * yv[3] +
      -8.7463975969 * yv[4] +
      4.5450474591 * yv[5];
    filtData[i] = yv[6];
  }

  xv.fill(0); // *: Reset to 0
  yv.fill(0);

  const backFiltData = new Array(dataLength);
  for (let j = dataLength - 1; j > -1; j -= 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = filtData[j] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] =
      xv[0] +
      xv[6] +
      6 * (xv[1] + xv[5]) +
      15 * (xv[2] + xv[4]) +
      20 * xv[3] +
      -0.2304689873 * yv[0] +
      1.7181353495 * yv[1] +
      -5.3861730594 * yv[2] +
      9.0983763199 * yv[3] +
      -8.7463975969 * yv[4] +
      4.5450474591 * yv[5];
    backFiltData[j] = yv[6];
  }
  return backFiltData;
};

/**
 * Low-pass filters an ECG channel recorded at 250Hz at a 20Hz cutoff. Runs a hardcoded order-6 IIR recurrence, scaling each input by the filter gain and carrying six delayed input and output taps forward across the samples, then repeats the recurrence over the result in reverse for zero phase shift.
 *
 * @param data - ECG samples for one channel.
 * @returns The samples with content above 20Hz attenuated.
 */
export const Lowpass20Hz = function (data: number[]) {
  const dataLength = data.length;
  const filtData = new Array(dataLength);

  const Nzeros = 6;
  const Npoles = 6;
  const GAIN = 9.339780497e3;
  const xv = new Array(Nzeros + 1).fill(0);
  const yv = new Array(Npoles + 1).fill(0);

  for (let i = 0; i < dataLength; i += 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = data[i] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] =
      xv[0] +
      xv[6] +
      6 * (xv[1] + xv[5]) +
      15 * (xv[2] + xv[4]) +
      20 * xv[3] +
      -0.1396600417 * yv[0] +
      1.1086708553 * yv[1] +
      -3.7230194289 * yv[2] +
      6.7850160254 * yv[3] +
      -7.0995038188 * yv[4] +
      4.0616439992 * yv[5];
    filtData[i] = yv[6];
  }

  xv.fill(0);
  yv.fill(0);

  const backFiltData = new Array(dataLength);
  for (let j = dataLength - 1; j > -1; j -= 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = filtData[j] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] =
      xv[0] +
      xv[6] +
      6 * (xv[1] + xv[5]) +
      15 * (xv[2] + xv[4]) +
      20 * xv[3] +
      -0.1396600417 * yv[0] +
      1.1086708553 * yv[1] +
      -3.7230194289 * yv[2] +
      6.7850160254 * yv[3] +
      -7.0995038188 * yv[4] +
      4.0616439992 * yv[5];
    backFiltData[j] = yv[6];
  }
  return backFiltData;
};

/**
 * Low-pass filters an ECG channel recorded at 250Hz at a 30Hz cutoff. Runs a hardcoded order-6 IIR recurrence, scaling each input by the filter gain and carrying six delayed input and output taps forward across the samples, then repeats the recurrence over the result in reverse for zero phase shift.
 *
 * @param data - ECG samples for one channel.
 * @returns The samples with content above 30Hz attenuated.
 */
export const Lowpass30Hz = function (data: number[]) {
  const dataLength = data.length;
  const filtData = new Array(dataLength);

  const Nzeros = 6;
  const Npoles = 6;
  const GAIN = 1.166131196e3;
  const xv = new Array(Nzeros + 1).fill(0);
  const yv = new Array(Npoles + 1).fill(0);

  for (let i = 0; i < dataLength; i += 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = data[i] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] =
      xv[0] +
      xv[6] +
      6 * (xv[1] + xv[5]) +
      15 * (xv[2] + xv[4]) +
      20 * xv[3] +
      -0.0495583579 * yv[0] +
      0.4411240091 * yv[1] +
      -1.685139137 * yv[2] +
      3.5565864008 * yv[3] +
      -4.4164369208 * yv[4] +
      3.0985416734 * yv[5];
    filtData[i] = yv[6];
  }

  xv.fill(0);
  yv.fill(0);

  const backFiltData = [];
  for (let j = dataLength - 1; j > -1; j -= 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = filtData[j] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] =
      xv[0] +
      xv[6] +
      6 * (xv[1] + xv[5]) +
      15 * (xv[2] + xv[4]) +
      20 * xv[3] +
      -0.0495583579 * yv[0] +
      0.4411240091 * yv[1] +
      -1.685139137 * yv[2] +
      3.5565864008 * yv[3] +
      -4.4164369208 * yv[4] +
      3.0985416734 * yv[5];
    backFiltData[j] = yv[6];
  }
  return backFiltData;
};

/**
 * Low-pass filters an ECG channel recorded at 250Hz at a 40Hz cutoff, the viewer's default. Runs a hardcoded order-6 IIR recurrence, scaling each input by the filter gain and carrying six delayed input and output taps forward across the samples, then repeats the recurrence over the result in reverse for zero phase shift.
 *
 * @param data - ECG samples for one channel.
 * @returns The samples with content above 40Hz attenuated.
 */
export const Lowpass40Hz = function (data: number[]) {
  const dataLength = data.length;
  const filtData = new Array(dataLength);

  const Nzeros = 6;
  const Npoles = 6;
  const GAIN = 2.826237084e2;
  const xv = new Array(Nzeros + 1).fill(0);
  const yv = new Array(Npoles + 1).fill(0);

  for (let i = 0; i < dataLength; i += 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = data[i] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] =
      xv[0] +
      xv[6] +
      6 * (xv[1] + xv[5]) +
      15 * (xv[2] + xv[4]) +
      20 * xv[3] +
      -0.0164394287 * yv[0] +
      0.1617798752 * yv[1] +
      -0.697562921 * yv[2] +
      1.6855996074 * yv[3] +
      -2.5005825661 * yv[4] +
      2.1407559242 * yv[5];
    filtData[i] = yv[6];
  }

  xv.fill(0);
  yv.fill(0);

  const backFiltData = new Array(dataLength);
  for (let j = dataLength - 1; j > -1; j -= 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = filtData[j] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] =
      xv[0] +
      xv[6] +
      6 * (xv[1] + xv[5]) +
      15 * (xv[2] + xv[4]) +
      20 * xv[3] +
      -0.0164394287 * yv[0] +
      0.1617798752 * yv[1] +
      -0.697562921 * yv[2] +
      1.6855996074 * yv[3] +
      -2.5005825661 * yv[4] +
      2.1407559242 * yv[5];
    backFiltData[j] = yv[6];
  }
  return backFiltData;
};

/**
 * Removes 50Hz mains interference from an ECG channel recorded at 250Hz. Runs a hardcoded order-12 IIR notch tuned to the 49-51Hz band, scaling each input by the filter gain and carrying twelve delayed input and output taps forward across the samples, then repeats the same recurrence over the result in reverse so the notch adds no phase shift.
 *
 * @param data - Raw ECG samples for one channel.
 * @returns The samples with the 50Hz band notched out.
 */
export const BandStop50Hz = function (data: number[]) {
  const dataLength = data.length;
  const filtData = new Array(dataLength);

  // *: 49-51Hz
  const Nzeros = 12;
  const Npoles = 12;
  const GAIN = 1.101990864;
  const xv = new Array(Nzeros + 1).fill(0);
  const yv = new Array(Npoles + 1).fill(0);

  for (let i = 0; i < dataLength; i += 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = xv[7];
    xv[7] = xv[8];
    xv[8] = xv[9];
    xv[9] = xv[10];
    xv[10] = xv[11];
    xv[11] = xv[12];
    xv[12] = data[i] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] = yv[7];
    yv[7] = yv[8];
    yv[8] = yv[9];
    yv[9] = yv[10];
    yv[10] = yv[11];
    yv[11] = yv[12];
    yv[12] =
      xv[0] +
      xv[12] -
      3.709375393 * (xv[1] + xv[11]) +
      11.733110753 * (xv[2] + xv[10]) -
      23.27271251 * (xv[3] + xv[9]) +
      40.12368027 * (xv[4] + xv[8]) -
      51.813135336 * (xv[5] + xv[7]) +
      58.83697284 * xv[6] +
      -0.8234628524 * yv[0] +
      3.1039671218 * yv[1] +
      -9.9760024913 * yv[2] +
      20.108031587 * yv[3] +
      -35.228420248 * yv[4] +
      46.232021149 * yv[5] +
      -53.353539857 * yv[6] +
      47.753071126 * yv[7] +
      -37.584606379 * yv[8] +
      22.158786354 * yv[9] +
      -11.355128923 * yv[10] +
      3.6493432008 * yv[11];
    filtData[i] = yv[12];
  }

  xv.fill(0);
  yv.fill(0);

  const backFiltData = new Array(dataLength);
  for (let j = dataLength - 1; j > -1; j -= 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = xv[7];
    xv[7] = xv[8];
    xv[8] = xv[9];
    xv[9] = xv[10];
    xv[10] = xv[11];
    xv[11] = xv[12];
    xv[12] = filtData[j] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] = yv[7];
    yv[7] = yv[8];
    yv[8] = yv[9];
    yv[9] = yv[10];
    yv[10] = yv[11];
    yv[11] = yv[12];
    yv[12] =
      xv[0] +
      xv[12] -
      3.709375393 * (xv[1] + xv[11]) +
      11.733110753 * (xv[2] + xv[10]) -
      23.27271251 * (xv[3] + xv[9]) +
      40.12368027 * (xv[4] + xv[8]) -
      51.813135336 * (xv[5] + xv[7]) +
      58.83697284 * xv[6] +
      -0.8234628524 * yv[0] +
      3.1039671218 * yv[1] +
      -9.9760024913 * yv[2] +
      20.108031587 * yv[3] +
      -35.228420248 * yv[4] +
      46.232021149 * yv[5] +
      -53.353539857 * yv[6] +
      47.753071126 * yv[7] +
      -37.584606379 * yv[8] +
      22.158786354 * yv[9] +
      -11.355128923 * yv[10] +
      3.6493432008 * yv[11];
    backFiltData[j] = yv[12];
  }
  return backFiltData;
};

/**
 * Removes 60Hz mains interference from an ECG channel recorded at 250Hz. Runs a hardcoded order-12 IIR notch tuned to the 59-61Hz band, scaling each input by the filter gain and carrying twelve delayed input and output taps forward across the samples, then repeats the same recurrence over the result in reverse so the notch adds no phase shift.
 *
 * @param data - Raw ECG samples for one channel.
 * @returns The samples with the 60Hz band notched out.
 */
export const BandStop60Hz = function (data: number[]) {
  const dataLength = data.length;
  const filtData = new Array(dataLength);

  // *: 59-61Hz
  const Nzeros = 12;
  const Npoles = 12;
  const GAIN = 1.101990864;
  const xv = new Array(Nzeros + 1).fill(0);
  const yv = new Array(Npoles + 1).fill(0);

  for (let i = 0; i < dataLength; i += 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = xv[7];
    xv[7] = xv[8];
    xv[8] = xv[9];
    xv[9] = xv[10];
    xv[10] = xv[11];
    xv[11] = xv[12];
    xv[12] = data[i] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] = yv[7];
    yv[7] = yv[8];
    yv[8] = yv[9];
    yv[9] = yv[10];
    yv[10] = yv[11];
    yv[11] = yv[12];
    yv[12] =
      xv[0] +
      xv[12] -
      0.7537242685 * (xv[1] + xv[11]) +
      6.2367084471 * (xv[2] + xv[10]) -
      3.8082686541 * (xv[3] + xv[9]) +
      15.950569181 * (xv[4] + xv[8]) -
      7.6563723167 * (xv[5] + xv[7]) +
      21.427725397 * xv[6] +
      -0.8234628524 * yv[0] +
      0.6307087045 * yv[1] +
      -5.3019906079 * yv[2] +
      3.2902156369 * yv[3] +
      -14.001835851 * yv[4] +
      6.8310619486 * yv[5] +
      -19.426635812 * yv[6] +
      7.0557977771 * yv[7] +
      -14.938241598 * yv[8] +
      3.6257652589 * yv[9] +
      -6.0349138523 * yv[10] +
      0.7415260639 * yv[11];
    filtData[i] = yv[12];
  }

  xv.fill(0);
  yv.fill(0);

  const backFiltData = new Array(dataLength);
  for (let j = dataLength - 1; j > -1; j -= 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = xv[7];
    xv[7] = xv[8];
    xv[8] = xv[9];
    xv[9] = xv[10];
    xv[10] = xv[11];
    xv[11] = xv[12];
    xv[12] = filtData[j] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] = yv[7];
    yv[7] = yv[8];
    yv[8] = yv[9];
    yv[9] = yv[10];
    yv[10] = yv[11];
    yv[11] = yv[12];
    yv[12] =
      xv[0] +
      xv[12] -
      0.7537242685 * (xv[1] + xv[11]) +
      6.2367084471 * (xv[2] + xv[10]) -
      3.8082686541 * (xv[3] + xv[9]) +
      15.950569181 * (xv[4] + xv[8]) -
      7.6563723167 * (xv[5] + xv[7]) +
      21.427725397 * xv[6] +
      -0.8234628524 * yv[0] +
      0.6307087045 * yv[1] +
      -5.3019906079 * yv[2] +
      3.2902156369 * yv[3] +
      -14.001835851 * yv[4] +
      6.8310619486 * yv[5] +
      -19.426635812 * yv[6] +
      7.0557977771 * yv[7] +
      -14.938241598 * yv[8] +
      3.6257652589 * yv[9] +
      -6.0349138523 * yv[10] +
      0.7415260639 * yv[11];
    backFiltData[j] = yv[12];
  }
  return backFiltData;
};

/**
 * Low-pass filters an ECG channel recorded at 250Hz at a 100Hz cutoff. Runs a hardcoded order-6 IIR recurrence, scaling each input by the filter gain and carrying six delayed input and output taps forward across the samples, then repeats the recurrence over the result in reverse for zero phase shift.
 *
 * @param data - ECG samples for one channel.
 * @returns The samples with content above 100Hz attenuated.
 */
export const Lowpass100Hz = function (data: number[]) {
  const dataLength = data.length;
  const filtData = new Array(dataLength);

  const Nzeros = 6;
  const Npoles = 6;
  const GAIN = 3.45534243;
  const xv = new Array(Nzeros + 1).fill(0);
  const yv = new Array(Npoles + 1).fill(0);

  for (let i = 0; i < dataLength; i += 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = data[i] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] =
      xv[0] +
      xv[6] +
      6 * (xv[1] + xv[5]) +
      15 * (xv[2] + xv[4]) +
      20 * xv[3] +
      -0.0837564796 * yv[0] +
      -0.7052741145 * yv[1] +
      -2.5294949058 * yv[2] +
      -4.9654152288 * yv[3] +
      -5.6586671659 * yv[4] +
      -3.5794347983 * yv[5];
    filtData[i] = yv[6];
  }

  xv.fill(0);
  yv.fill(0);

  const backFiltData = new Array(dataLength);
  for (let j = dataLength - 1; j > -1; j -= 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = filtData[j] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] =
      xv[0] +
      xv[6] +
      6 * (xv[1] + xv[5]) +
      15 * (xv[2] + xv[4]) +
      20 * xv[3] +
      -0.0837564796 * yv[0] +
      -0.7052741145 * yv[1] +
      -2.5294949058 * yv[2] +
      -4.9654152288 * yv[3] +
      -5.6586671659 * yv[4] +
      -3.5794347983 * yv[5];
    backFiltData[j] = yv[6];
  }
  return backFiltData;
};

// * Highpass - Order 3
// * Lowpass - Order 6
// * Bandstop - Order 3 - 49-51 and 59-61
const FILTER_COEFFICIENT = {
  250: {
    high: {
      0.05: {
        a: [1, -2.997486726042495, 2.9949766093657826, -0.9974898813413775],
        b: [0.9987441520937068, -2.9962324562811204, 2.9962324562811204, -0.9987441520937068],
      },
      0.5: {
        a: [1.0, -2.974867424113648, 2.9500496793274675, -0.9751802955156089],
        b: [0.9875121748695905, -2.9625365246087716, 2.9625365246087716, -0.9875121748695905],
      },
      1: {
        a: [1.0, -2.9497358397063476, 2.9007269883554376, -0.9509756650162488],
        b: [0.9751798116347543, -2.925539434904263, 2.925539434904263, -0.9751798116347543],
      },
      2: {
        a: [1.0, -2.899479594611864, 2.8039479773829994, -0.9043475313924088],
        b: [0.9509718879234093, -2.8529156637702275, 2.8529156637702275, -0.9509718879234093],
      },
    },
  },
  170: {
    high: {
      0.05: {
        a: [1.0, -2.996304009168756, 2.992614845356099, -0.996310829887922],
        b: [0.998153710551597, -2.994461131654791, 2.994461131654791, -0.998153710551597],
      },
      0.5: {
        a: [1.0, -2.963040612183736, 2.926761067797223, -0.963714259575438],
        b: [0.981689492444549, -2.945068477333648, 2.945068477333648, -0.981689492444549],
      },
      1: {
        a: [1.0, -2.926084375007473, 2.854875290255163, -0.928742242174557],
        b: [0.963712738429649, -2.891138215288948, 2.891138215288948, -0.963712738429649],
      },
      2: {
        a: [1.0, -2.852193844468077, 2.715109820022557, -0.86254034060976],
        b: [0.928730500637549, -2.786191501912648, 2.786191501912648, -0.928730500637549],
      },
    },
    low: {
      15: {
        a: [
          1.0, -3.862933707472402, 6.481695971011082, -5.980931791480181, 3.182280355742947,
          -0.921988680506405, 0.113307353386523,
        ],
        b: [
          0.000178585948149, 0.001071515688896, 0.002678789222241, 0.003571718962988,
          0.002678789222241, 0.001071515688896, 0.000178585948149,
        ],
      },
      30: {
        a: [
          1.0, -1.747798009347676, 1.92329535255647, -1.189965309417062, 0.471052742364758,
          -0.103553149677336, 0.010175971670517,
        ],
        b: [
          0.005675118721089, 0.034050712326532, 0.085126780816329, 0.113502374421772,
          0.085126780816329, 0.034050712326532, 0.005675118721089,
        ],
      },
      40: {
        a: [
          1.0, -0.349078995959095, 0.823177846494546, -0.16769869329345, 0.126072620021544,
          -0.011380442348548, 0.001995269009954,
        ],
        b: [
          0.022235743811327, 0.133414462867964, 0.33353615716991, 0.444714876226547,
          0.33353615716991, 0.133414462867964, 0.022235743811327,
        ],
      },
    },
    band: {
      50: {
        a: [
          1.0, 1.602623361327744, 3.708585696008452, 3.201799525012913, 3.530295336713757,
          1.452152910829857, 0.86254034060976,
        ],
        b: [
          0.928730500637549, 1.525997158935154, 3.621980186028434, 3.204581479300204,
          3.621980186028435, 1.525997158935154, 0.928730500637549,
        ],
      },
      60: {
        a: [
          1.0, 3.529144903197384, 7.00506606923467, 8.344102070469448, 6.668161550033433,
          3.197793173108738, 0.862540340609761,
        ],
        b: [
          0.92873050063755, 3.360405960442216, 6.83915347930138, 8.350228225891135,
          6.83915347930138, 3.360405960442215, 0.928730500637549,
        ],
      },
      pantompkin: {
        a: [
          1.0, -4.994171687232088, 10.638455284704802, -12.375194854131115, 8.294067454243731,
          -3.037825668073298, 0.475417482401089,
        ],
        b: [
          0.004504022640265, 0.0, -0.013512067920796, 0.0, 0.013512067920796, 0.0,
          -0.004504022640265,
        ],
      },
    },
  },
  200: {
    high: {
      0.05: {
        a: [1.0, -2.996858407669392, 2.99372174820208, -0.996863336662985],
        b: [0.998430436566807, -2.995291309700421, 2.995291309700421, -0.998430436566807],
      },
      0.5: {
        a: [1.0, -2.968584396371833, 2.937660325278698, -0.969072113292553],
        b: [0.984414604367886, -2.953243813103657, 2.953243813103657, -0.984414604367886],
      },
      1: {
        a: [1.0, -2.937170728449891, 2.876299723479331, -0.939098940325283],
        b: [0.969071174031813, -2.907213522095439, 2.907213522095439, -0.969071174031813],
      },
      2: {
        a: [1.0, -2.874356892677484, 2.756483195225695, -0.881893130592486],
        b: [0.939091652311958, -2.817274956935874, 2.817274956935874, -0.939091652311958],
      },
    },
    low: {
      15: {
        a: [
          1.0, -4.182389579168495, 7.491611084587639, -7.313595966890727, 4.089349931833103,
          -1.238525371776707, 0.158427632551783,
        ],
        b: [
          0.000076214549009, 0.000457287294056, 0.00114321823514, 0.001524290980186,
          0.00114321823514, 0.000457287294056, 0.000076214549009,
        ],
      },
      30: {
        a: [
          1.0, -2.379721044554775, 2.910406567864687, -2.055131436773097, 0.877923897634089,
          -0.20986545035969, 0.021831573979972,
        ],
        b: [
          0.002585064184237, 0.015510385105424, 0.038775962763559, 0.051701283684746,
          0.038775962763559, 0.015510385105424, 0.002585064184237,
        ],
      },
      40: {
        a: [
          1.0, -1.187600680175615, 1.30521334928855, -0.674327525297998, 0.263469348280138,
          -0.051753033879641, 0.005022526595088,
        ],
        b: [
          0.010312874762664, 0.061877248575986, 0.154693121439966, 0.206257495253288,
          0.154693121439966, 0.061877248575986, 0.010312874762664,
        ],
      },
    },
    band: {
      50: {
        a: [
          1.0, -0.000000000000001, 2.874356892677484, -0.000000000000001, 2.756483195225695,
          -0.000000000000001, 0.881893130592485,
        ],
        b: [
          0.939091652311958, -0.000000000000001, 2.817274956935874, -0.000000000000001,
          2.817274956935874, -0.000000000000001, 0.939091652311958,
        ],
      },
      60: {
        a: [
          1.0, 1.816172282247672, 3.974090479018471, 3.70378656445392, 3.811081705817391,
          1.670184180831854, 0.881893130592485,
        ],
        b: [
          0.939091652311958, 1.742031266311199, 3.894441005402216, 3.706080494911048,
          3.894441005402216, 1.742031266311199, 0.939091652311958,
        ],
      },
      pantompkin: {
        a: [
          1.0, -5.17477792854435, 11.347101679049707, -13.497701826854337, 9.18842384942981,
          -3.394823429726714, 0.532075368312092,
        ],
        b: [
          0.002898194633721, 0.0, -0.008694583901164, 0.0, 0.008694583901164, 0.0,
          -0.002898194633721,
        ],
      },
    },
  },
  1000: {
    high: {
      0.05: {
        a: [1.0, -2.999371681471866, 2.998743560320315, -0.999371878817452],
        b: [0.999685890076204, -2.999057670228612, 2.999057670228612, -0.999685890076204],
      },
      0.5: {
        a: [1.0, -2.993716817276653, 2.987453358242849, -0.993736510057099],
        b: [0.996863335697075, -2.990590007091225, 2.990590007091225, -0.996863335697075],
      },
      1: {
        a: [1.0, -2.987433650055722, 2.974946132665443, -0.987512236110736],
        b: [0.993736502353987, -2.981209507061962, 2.981209507061962, -0.993736502353987],
      },
      2: {
        a: [1.0, -2.974867424113648, 2.950049679327468, -0.975180295515609],
        b: [0.987512174869591, -2.962536524608772, 2.962536524608772, -0.987512174869591],
      },
    },
    low: {
      15: {
        a: [
          1.0, -5.635880915490254, 13.245104571761694, -16.614170156135593, 11.73124679193344,
          -4.420971029104481, 0.694671323775999,
        ],
        b: [
          0.000000009167825, 0.00000005500695, 0.000000137517376, 0.000000183356501,
          0.000000137517376, 0.00000005500695, 0.000000009167825,
        ],
      },
      30: {
        a: [
          1.0, -5.271918566723347, 11.619927717930013, -13.702695926669747, 9.116066297447896,
          -3.243419845060288, 0.482072025618542,
        ],
        b: [
          0.000000495352235, 0.000002972113413, 0.000007430283531, 0.000009907044708,
          0.000007430283531, 0.000002972113413, 0.000000495352235,
        ],
      },
      40: {
        a: [
          1.0, -5.029438351421609, 10.607042183779685, -11.999315816216695, 7.675474548200204,
          -2.63105512847395, 0.377452386374089,
        ],
        b: [
          0.000002497222527, 0.000014983335161, 0.000037458337903, 0.000049944450538,
          0.000037458337903, 0.000014983335161, 0.000002497222527,
        ],
      },
      100: {
        a: [
          1.0, -3.579434798331192, 5.658667165933626, -4.96541522877857, 2.529494905841447,
          -0.705274114509901, 0.083756479618679,
        ],
        b: [
          0.00034053765272, 0.002043225916321, 0.005108064790802, 0.006810753054403,
          0.005108064790802, 0.002043225916321, 0.00034053765272,
        ],
      },
    },
    band: {
      50: {
        a: [
          1.0, -5.682548766350531, 13.738748579390284, -18.066456475718454, 13.624130527625166,
          -5.588128999639327, 0.975180295515609,
        ],
        b: [
          0.987512174869591, -5.635190566820433, 13.681517526395934, -18.066753108067434,
          13.681517526395931, -5.63519056682043, 0.98751217486959,
        ],
      },
      60: {
        a: [
          1.0, -5.555400896098081, 13.262450616882999, -17.36820879890897, 13.151806197177692,
          -5.463093785650935, 0.975180295515609,
        ],
        b: [
          0.98751217486959, -5.509102343296383, 13.207206379918556, -17.368498794065214,
          13.207206379918558, -5.509102343296384, 0.98751217486959,
        ],
      },
      pantompkin: {
        a: [
          1.0, -5.865659540117195, 14.34551076686175, -18.72441994035092, 13.75683995502362,
          -5.394164347627243, 0.881893130592486,
        ],
        b: [
          0.000029146494466, 0.0, -0.000087439483397, 0.0, 0.000087439483397, 0.0,
          -0.000029146494466,
        ],
      },
    },
  },
};

/**
 * Applies a tabulated Butterworth filter to an ECG signal without introducing phase shift. Looks the a and b coefficients up by sampling rate, filter type and cutoff and returns the signal untouched when no entry exists, otherwise prepends a mirrored copy of the leading samples as padding, runs the difference equation forward, reverses and runs it again, and trims the padding back off.
 *
 * @param signal - ECG samples to filter; mutated by the padding prepended to it.
 * @param samplingRate - Samples per second, used to select the coefficient table.
 * @param freq - Cutoff or notch frequency in Hz.
 * @param type - Filter shape to apply: high, low or band.
 * @returns The zero-phase filtered samples, or the input unchanged when no coefficients match.
 */
export const butterworthFilter = function ({
  signal,
  samplingRate,
  freq,
  type,
}: {
  signal: number[];
  samplingRate: number;
  freq: number;
  type: 'high' | 'low' | 'band';
}) {
  const filterCoeffs = (FILTER_COEFFICIENT as any)[samplingRate]?.[type]?.[freq];
  if (!filterCoeffs) {
    return signal;
  }

  const CA = filterCoeffs.a;
  const CB = filterCoeffs.b;

  const windown = CA.length * 3 * 3;

  const padding = signal.slice(0, windown);
  padding.unshift(...signal.slice(1, windown).reverse());
  padding.forEach((v) => {
    signal.unshift(v);
  });

  let xv = new Array(CA.length).fill(0);
  let yv = new Array(CA.length).fill(0);

  const bufSignal: number[] = [];
  signal.forEach((v) => {
    xv[0] = v;
    yv[0] = CB[0] * xv[0];

    for (let j = 0; j < CB.length; j += 1) {
      yv[0] += CB[j] * xv[j] - CA[j] * yv[j];
    }

    for (let j = xv.length - 1; j > 0; j -= 1) {
      xv[j] = xv[j - 1];
      yv[j] = yv[j - 1];
    }
    bufSignal.push(yv[0]);
  });
  bufSignal.reverse();

  xv = new Array(CA.length).fill(0);
  yv = new Array(CA.length).fill(0);

  let bufSignalReverse: number[] = [];
  bufSignal.forEach((v) => {
    xv[0] = v;
    yv[0] = CB[0] * xv[0];
    for (let j = 0; j < CB.length; j += 1) {
      yv[0] += CB[j] * xv[j] - CA[j] * yv[j];
    }

    for (let j = xv.length - 1; j > 0; j -= 1) {
      xv[j] = xv[j - 1];
      yv[j] = yv[j - 1];
    }
    bufSignalReverse.push(yv[0]);
  });
  bufSignalReverse.reverse();
  bufSignalReverse = bufSignalReverse.slice(padding.length, bufSignalReverse.length);

  return bufSignalReverse;
};

/**
 * High-pass filters an ECG channel to remove baseline wander. Copies the samples so the caller's array is not mutated by the filter's padding step and delegates to the tabulated Butterworth routine in high-pass mode.
 *
 * @param data - ECG samples for one channel.
 * @param freq - High-pass cutoff frequency in Hz.
 * @param samplingRate - Samples per second of the recording.
 * @returns The high-pass filtered samples.
 */
export const Highpass = function (data: number[], freq: number, samplingRate: number) {
  const cloneData = [...data];
  const filtData = butterworthFilter({
    signal: cloneData,
    samplingRate,
    freq,
    type: 'high',
  });
  return filtData;
};

/**
 * Applies the low-pass or mains-notch stage the ECG viewer's filter setting asks for. At 250Hz it dispatches to the hardcoded 15, 20, 30, 40 and 100Hz low-pass routines or the 50 and 60Hz notches, falling back to the pass-through stage for any other setting; at every other sampling rate it copies the samples and defers to the tabulated Butterworth routine, choosing band mode for 50 and 60Hz and low mode otherwise.
 *
 * @param data - ECG samples for one channel.
 * @param freq - Selected cutoff or notch frequency in Hz.
 * @param samplingRate - Samples per second of the recording.
 * @returns The filtered samples for the requested cutoff.
 */
export const Lowpass = function (data: number[], freq: number, samplingRate: number) {
  switch (samplingRate) {
    case 250: {
      let filtData;
      switch (freq) {
        case 15: {
          filtData = Lowpass15Hz(data);
          break;
        }
        case 20: {
          filtData = Lowpass20Hz(data);
          break;
        }
        case 30: {
          filtData = Lowpass30Hz(data);
          break;
        }
        case 40: {
          filtData = Lowpass40Hz(data);
          break;
        }
        case 50: {
          filtData = BandStop50Hz(data);
          break;
        }
        case 60: {
          filtData = BandStop60Hz(data);
          break;
        }
        case 100: {
          filtData = Lowpass100Hz(data);
          break;
        }
        default: {
          filtData = Lowpass0Hz(data);
          break;
        }
      }
      return filtData;
    }
    default: {
      const cloneData = [...data];
      const type = freq === 50 || freq === 60 ? 'band' : 'low';
      const filtData = butterworthFilter({
        signal: cloneData,
        samplingRate,
        freq,
        type,
      });
      return filtData;
    }
  }
};
